// CRM metadata for the mapping editor: the client's real properties/custom fields, deal pipelines + stages, owners and
// object types, read live through the stored connection (tokens never leave the server). Every section reports its own
// honest state — a missing private-app scope names the exact scopes to add; a provider outage is «no disponible»; an
// option is never fabricated. Provider shapes are from the public docs and are UNVERIFIED until a real account.
import { z } from 'zod';
import { CrmProviderSchema } from './api-crm.js';

const Iso = z.string();

export const CRM_METADATA_SECTIONS = ['contactProperties', 'dealProperties', 'pipelines', 'owners', 'objectTypes'] as const;
export const CrmMetadataSectionKeySchema = z.enum(CRM_METADATA_SECTIONS);
export type CrmMetadataSectionKey = z.infer<typeof CrmMetadataSectionKeySchema>;

export const CRM_METADATA_SECTION_LABELS: Record<CrmMetadataSectionKey, string> = {
  contactProperties: 'Propiedades del contacto',
  dealProperties: 'Propiedades del negocio / oportunidad',
  pipelines: 'Pipelines y etapas',
  owners: 'Responsables',
  objectTypes: 'Tipos de registro',
};

/**
 * ok              — read from the CRM (items may legitimately be empty)
 * missing_scope   — the CRM refused for lack of permission; `scopes` lists what to add to the private app
 * credentials_rejected — the token was rejected (revoked/expired); reconnect
 * unavailable     — the CRM did not answer / answered with an error; try «Actualizar» later
 * unsupported     — this provider has no such section
 */
export const CRM_METADATA_STATUSES = ['ok', 'missing_scope', 'credentials_rejected', 'unavailable', 'unsupported'] as const;
export const CrmMetadataStatusSchema = z.enum(CRM_METADATA_STATUSES);
export type CrmMetadataStatus = z.infer<typeof CrmMetadataStatusSchema>;

/** Normalized value type of a CRM field (raw provider type kept in `type`). */
export const CRM_FIELD_KINDS = ['string', 'number', 'enumeration', 'date', 'datetime', 'bool', 'other'] as const;
export const CrmFieldKindSchema = z.enum(CRM_FIELD_KINDS);
export type CrmFieldKind = z.infer<typeof CrmFieldKindSchema>;

export const CrmFieldOptionSchema = z.object({ label: z.string(), value: z.string() });
export type CrmFieldOption = z.infer<typeof CrmFieldOptionSchema>;

export const CrmFieldMetaSchema = z.object({
  /** What the mapping stores: HubSpot internal property name / HighLevel custom field id. */
  name: z.string(),
  label: z.string(),
  /** Raw provider type (HubSpot `type`, HighLevel `dataType`). */
  type: z.string(),
  kind: CrmFieldKindSchema,
  /** HubSpot `fieldType` / HighLevel `fieldKey` (shown as a hint). */
  fieldType: z.string().nullable(),
  group: z.string().nullable(),
  /** Enumeration options (hidden options removed). */
  options: z.array(CrmFieldOptionSchema),
  /** The CRM computes it (calculated / rollup): never a write target. */
  calculated: z.boolean(),
  /** The CRM does not accept writes to it. */
  readOnly: z.boolean(),
});
export type CrmFieldMeta = z.infer<typeof CrmFieldMetaSchema>;

export const CrmPipelineMetaSchema = z.object({
  id: z.string(),
  label: z.string(),
  displayOrder: z.number().int(),
  stages: z.array(z.object({ id: z.string(), label: z.string(), displayOrder: z.number().int() })),
});
export type CrmPipelineMeta = z.infer<typeof CrmPipelineMetaSchema>;

export const CrmOwnerMetaSchema = z.object({ id: z.string(), name: z.string().nullable(), email: z.string().nullable() });
export type CrmOwnerMeta = z.infer<typeof CrmOwnerMetaSchema>;

export const CrmObjectTypeMetaSchema = z.object({
  /** Value stored in `inventory.objectType` (deals, contacts, a custom object id such as 2-123456 / p_apartados). */
  id: z.string(),
  label: z.string(),
  builtin: z.boolean(),
  /** Custom objects: their properties (built-ins use the contact / deal property sections). */
  properties: z.array(CrmFieldMetaSchema),
});
export type CrmObjectTypeMeta = z.infer<typeof CrmObjectTypeMetaSchema>;

const section = <T extends z.ZodType>(item: T) =>
  z.object({
    status: CrmMetadataStatusSchema,
    items: z.array(item),
    /** missing_scope: the exact scopes to add (private app / private integration). */
    scopes: z.array(z.string()),
    detail: z.string().nullable(),
    /** When the items were read from the CRM (null when not read). */
    fetchedAt: Iso.nullable(),
    /** Served from the short cache (use «Actualizar» to re-read). */
    cached: z.boolean(),
  });

export const CrmMetadataSchema = z.object({
  connectionId: z.string(),
  provider: CrmProviderSchema,
  sections: z.object({
    contactProperties: section(CrmFieldMetaSchema),
    dealProperties: section(CrmFieldMetaSchema),
    pipelines: section(CrmPipelineMetaSchema),
    owners: section(CrmOwnerMetaSchema),
    objectTypes: section(CrmObjectTypeMetaSchema),
  }),
});
export type CrmMetadata = z.infer<typeof CrmMetadataSchema>;
export type CrmMetadataSection<K extends CrmMetadataSectionKey> = CrmMetadata['sections'][K];

export const CrmMetadataQuerySchema = z.object({
  /** «Actualizar campos del CRM»: bypass the cache and re-read every section. */
  refresh: z.union([z.boolean(), z.enum(['true', 'false', '1', '0'])]).default(false).transform((v) => v === true || v === 'true' || v === '1'),
});

/**
 * Scopes of the HubSpot PRIVATE APP Estateflow deploys in each client portal (owner decision 2026-10-15). Required ones
 * are needed to connect; the optional ones enable a feature and the app keeps working without them.
 */
export const HUBSPOT_PRIVATE_APP_SCOPES: readonly { scope: string; required: boolean; purpose: string }[] = [
  { scope: 'crm.objects.contacts.read', required: true, purpose: 'Leer contactos' },
  { scope: 'crm.objects.contacts.write', required: true, purpose: 'Crear y actualizar contactos, notas, llamadas y tareas' },
  { scope: 'crm.schemas.contacts.read', required: false, purpose: 'Elegir propiedades del contacto en el mapeo' },
  { scope: 'crm.objects.deals.read', required: false, purpose: 'Leer negocios, pipelines y etapas' },
  { scope: 'crm.objects.deals.write', required: false, purpose: 'Crear y actualizar negocios por proyecto' },
  { scope: 'crm.schemas.deals.read', required: false, purpose: 'Elegir propiedades del negocio en el mapeo' },
  { scope: 'crm.objects.owners.read', required: false, purpose: 'Elegir responsables (owners) en el mapeo' },
  { scope: 'sales-email-read', required: false, purpose: 'Leer los correos del historial del contacto antes de escribirle' },
  { scope: 'scheduler.meetings.meeting-link.read', required: false, purpose: 'Enlaces de reunión de HubSpot como agenda (enlaces y disponibilidad)' },
  { scope: 'crm.schemas.custom.read', required: false, purpose: 'Inventario en objetos personalizados (Enterprise): tipos y propiedades' },
  { scope: 'crm.objects.custom.read', required: false, purpose: 'Inventario en objetos personalizados (Enterprise): leer registros' },
  { scope: 'conversations.read', required: false, purpose: 'Puente de WhatsApp a la bandeja de HubSpot' },
  { scope: 'conversations.custom_channels.read', required: false, purpose: 'Puente de WhatsApp a la bandeja de HubSpot' },
  { scope: 'conversations.custom_channels.write', required: false, purpose: 'Puente de WhatsApp a la bandeja de HubSpot' },
];
